import React, { useState } from 'react'
import styles from './version-info.module.scss'
import { isNativeApp } from 'utils/nativeApp'

/**
 * Component to display version information and update option
 */
export default function VersionInfo({ isUpdateAvailable, onClose }) {
    const [isRefreshing, setIsRefreshing] = useState(false)
    const nativeApp = isNativeApp()
    const [webVersion, setWebVersion] = useState(() => window.Knov?.version || null)
    const [nativeVersionInfo, setNativeVersionInfo] = useState(() => ({
        nativeRuntimeInfo: window.KNOVIGATOR_RN_INFO || null,
        nativeAppInfo: window.KNOVIGATOR_NATIVE_APP_INFO || null,
    }))
    const nativeRuntimeInfo = nativeVersionInfo.nativeRuntimeInfo
    const nativeAppInfo = nativeVersionInfo.nativeAppInfo

    React.useEffect(() => {
        const syncWebVersion = () => setWebVersion(window.Knov?.version || null)
        const syncNativeVersionInfo = () =>
            setNativeVersionInfo({
                nativeRuntimeInfo: window.KNOVIGATOR_RN_INFO || null,
                nativeAppInfo: window.KNOVIGATOR_NATIVE_APP_INFO || null,
            })

        const handleVersionDataUpdate = () => syncWebVersion()
        const handleNativeVersionInfoChange = () => syncNativeVersionInfo()
        window.addEventListener('versionDataUpdate', handleVersionDataUpdate)
        window.addEventListener('nativeVersionInfoChange', handleNativeVersionInfoChange)
        syncWebVersion()
        syncNativeVersionInfo()

        if (window.KNOVIGATOR_NATIVE_CAPS?.versionInfo) {
            window.ReactNativeWebView.postMessage(
                JSON.stringify({ kind: 'requestNativeVersionInfo' }),
            )
        }

        return () => {
            window.removeEventListener('versionDataUpdate', handleVersionDataUpdate)
            window.removeEventListener('nativeVersionInfoChange', handleNativeVersionInfoChange)
        }
    }, [])

    const formatRelativeTime = timestamp => {
        if (!timestamp) return 'Unknown'
        const diffMs = timestamp - Date.now()
        const absDiffMs = Math.abs(diffMs)
        const minutes = Math.round(diffMs / (60 * 1000))
        const hours = Math.round(diffMs / (60 * 60 * 1000))
        const days = Math.round(diffMs / (24 * 60 * 60 * 1000))
        const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })

        if (absDiffMs < 60 * 60 * 1000) return rtf.format(minutes, 'minute')
        if (absDiffMs < 24 * 60 * 60 * 1000) return rtf.format(hours, 'hour')
        return rtf.format(days, 'day')
    }

    const webLabel = webVersion?.buildTimeUnixTimestamp
        ? `Web (${formatRelativeTime(webVersion.buildTimeUnixTimestamp)}):`
        : 'Web:'
    const webValue = webVersion?.gitCommitHash?.substring(0, 7) || 'Unknown'
    const rnLabel =
        nativeRuntimeInfo?.channel && nativeRuntimeInfo.channel !== 'production'
            ? `RN (${nativeRuntimeInfo.channel}):`
            : 'RN:'
    const expoVersion = nativeRuntimeInfo?.expoVersion || null
    const rnValue =
        expoVersion && nativeRuntimeInfo?.runtimeVersion
            ? `v${expoVersion}-r${nativeRuntimeInfo.runtimeVersion}`
            : expoVersion
            ? `v${expoVersion}`
            : nativeRuntimeInfo?.runtimeVersion
            ? `r${nativeRuntimeInfo.runtimeVersion}`
            : nativeRuntimeInfo?.updateId?.substring?.(0, 7) || 'N/A'
    const nativePlatform = nativeAppInfo?.platform === 'android' ? 'Android' : 'iOS'
    const nativeValue =
        nativeAppInfo?.version && nativeAppInfo?.build
            ? `v${nativeAppInfo.version}-b${nativeAppInfo.build}`
            : nativeAppInfo?.version
            ? `v${nativeAppInfo.version}`
            : 'N/A'

    const handleRefresh = () => {
        setIsRefreshing(true)
        setTimeout(() => {
            window.location.reload()
        }, 500)
    }

    return (
        <div className={styles.versionInfoOverlay} onClick={onClose}>
            <div className={styles.versionInfoContainer} onClick={e => e.stopPropagation()}>
                <div className={styles.versionInfoHeader}>
                    <h3>About Treechat</h3>
                    <button className={styles.closeButton} onClick={onClose}>
                        ×
                    </button>
                </div>
                <div className={styles.versionInfoContent}>
                    <div className={styles.versionRow}>
                        <span className={styles.versionLabel}>{webLabel}</span>
                        <span className={styles.versionValue}>{webValue}</span>
                    </div>
                    {nativeApp && (
                        <div className={styles.versionRow}>
                            <span className={styles.versionLabel}>{rnLabel}</span>
                            <span className={styles.versionValue}>{rnValue}</span>
                        </div>
                    )}
                    {nativeApp && (
                        <div className={styles.versionRow}>
                            <span className={styles.versionLabel}>{nativePlatform}:</span>
                            <span className={styles.versionValue}>{nativeValue}</span>
                        </div>
                    )}

                    {isUpdateAvailable && (
                        <div className={styles.updateSection}>
                            <div className={styles.updateMessage}>A newer version is available</div>
                            <button
                                className={styles.updateButton}
                                onClick={handleRefresh}
                                disabled={isRefreshing}
                            >
                                {isRefreshing ? 'Refreshing...' : 'Update to latest'}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
